<script lang="ts">
	import { get } from 'svelte/store';
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Button,
		Modal,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
		Text,
		SelectCard,
		Row,
		Col,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { devices } from 'src/stores/lists/devices';
	import { real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar, deepCopy } from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		createVirtualTemplate,
		getVirtualTemplate,
		updateVirtualTemplate,
	} from 'src/helpers/virtual_helpers/template_helpers/virtual_template_helpers';
	import { getVirtualComponents } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { getInitVCSettings } from 'src/helpers/virtual_helpers/virtual_components_settings_helpers';
	//Components
	import {
		buildVCTemplateWizard,
		getVirtualTemplateList,
	} from 'src/helpers/virtual_helpers/template_helpers/virtual_template_config';
	import CreateTemplate from './CreateTemplate.svelte';
	import PreviewTemplate from './PreviewTemplate.svelte';
	import VCNameSettings from '../../Settings/VCNameSettings.svelte';
	import DeleteTemplateModal from './DeleteTemplateModal.svelte';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import { validateVirtualTemplate } from 'src/helpers/virtual_helpers/template_helpers/virtual_template_validation';
	//Types
	import type { Device } from 'src/types/Device';
	import type {
		VirtualDeviceTemplate,
		VirtualTemplateType,
	} from 'src/helpers/virtual_helpers/template_helpers/virtual_template_types';
	import type { VirtualComponentInterface } from 'src/types/VirtualComponents';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device: Device;
	export let groupComponent: string;
	export let showMainModal: boolean;
	export let showCreateTemplateModal: boolean;

	const parentDevice: Device = deepCopy(device);
	const templateList = getVirtualTemplateList();
	const virtualDeviceID = groupComponent ? groupComponent.split(':')[1] : null;
	const virtualDevice: Device = virtualDeviceID
		? get(devices)[`${real_id(parentDevice?.id)}_${virtualDeviceID}`]
		: null;
	const virtualTemplateDevice: Device = virtualDevice ? deepCopy(virtualDevice) : null;
	const deviceSettings: DeviceSettingsG2 = devices_settings.getByRealId(parentDevice.id);
	const deviceTemplate: VirtualDeviceTemplate = getVirtualTemplate(virtualTemplateDevice);

	const onSelectTemplate = (type: VirtualTemplateType) => {
		virtualTemplate = deviceTemplate?.type === type ? deviceTemplate : { type };
	};

	const saveTemplate = async () => {
		waitForResponse = true;
		const resToastMessages = {
			success: $format('virtual_template_created_success', {
				default: 'Virtual template created successfully.',
			}),
			danger: null,
		};
		const res: SettingRequest = virtualTemplateDevice
			? await updateVirtualTemplate(
					virtualTemplateDevice,
					parentDevice,
					groupComponent,
					virtualTemplate,
					groupSettings,
				)
			: await createVirtualTemplate(parentDevice, groupComponent, virtualTemplate, groupSettings);
		if (res?.isok) {
			showCreateTemplateModal = false;
			showMainModal = false;
		} else if (res?.error?.device_error?.code === -108) {
			resToastMessages.danger = $format('vc_template_create_reached_error', {
				default: 'Reached the maximum 10 of virtual components.',
			});
		}
		onSettingsResponse(res, parentDevice, resToastMessages);
		waitForResponse = false;
	};

	let waitForResponse = false;
	let showDeleteModal = false;
	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	let templateWizard = buildVCTemplateWizard();
	let templateConfigStep = templateWizard[0];
	let virtualTemplate: VirtualDeviceTemplate = deepCopy(deviceTemplate || {});
	let groupSettings: VirtualComponentInterface['config'] =
		deviceSettings?.[groupComponent] || getInitVCSettings('group');
</script>

<Modal
	title={$format('vc_template_wizard_title', {
		default: 'Create Virtual Template Device',
	})}
	showModal={showCreateTemplateModal}
	on:close={() => {
		if (JSON.stringify(virtualTemplate) !== JSON.stringify(deviceTemplate || {})) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			showCreateTemplateModal = false;
		}
	}}
	removeScrollPadding
>
	<Wizard
		slot="toolbar"
		activeStepNumber={templateConfigStep.activeIndex}
		stepsTotal={templateWizard.length}
		activeStepTitle={templateConfigStep.title}
	>
		{#each templateWizard as item, i (i)}
			<WizardItem
				on:click={() => {
					templateConfigStep = templateWizard[i];
				}}
				active={templateConfigStep.activeIndex === i}
				title={item.title}
				disabled={waitForResponse ||
					(i <= 2 && i > 0 && !virtualTemplate?.type) ||
					(i === 2 && !validateVirtualTemplate(virtualTemplate))}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div class:flex-overflow-hidden={false} slot="content">
		<WizardContainer activeStepIndex={templateConfigStep.activeIndex} adaptiveHeight={true}>
			{#each templateWizard as item, i (i)}
				<WizardContent active={templateConfigStep.activeIndex === i} class="pb-s">
					{#if item.subtitle}
						<Text element="h2" class="mb-s">
							{item.subtitle}
						</Text>
					{/if}
					{#if i == 0}
						<VCNameSettings
							bind:settings={groupSettings}
							label={groupComponent ||
								`group:${200 + getVirtualComponents(parentDevice.id, 'group').length}`}
							title={$format('vc_template_name', {
								default: 'Template name',
							})}
						/>
						<Row>
							{#each Object.values(templateList) as template}
								<Col sm="12" md="6">
									<SelectCard
										cardBgColor={$showNewLayout ? 'color-1' : undefined}
										type="horizontal"
										title={capFirstChar(template.title)}
										descriptiveText={capFirstChar(template.description)}
										selected={virtualTemplate.type == template.type}
										variant="radio"
										icon={template.icon}
										class="width-full"
										on:click={() => onSelectTemplate(template.type)}
									/>
								</Col>
							{/each}
						</Row>
						<Row>
							<Col sm="12" md="6">
								{#if deviceTemplate}
									<Button
										size="m"
										class="mt-s"
										aria-label={$format('button_delete', {
											default: 'Delete button',
										})}
										icon="fas fa-trash"
										variant="danger"
										on:click={() => (showDeleteModal = true)}
									>
										{$format('vc_template_delete_template', {
											default: 'Delete Virtual Template Device',
										})}
									</Button>
								{/if}
							</Col>
						</Row>
					{:else if i == 1 && templateConfigStep.activeIndex === 1 && virtualTemplate?.type}
						<CreateTemplate {parentDevice} bind:virtualTemplate />
					{:else if i == 2 && templateConfigStep.activeIndex === 2 && virtualTemplate?.type}
						<PreviewTemplate
							device={parentDevice}
							{virtualTemplate}
							templateName={groupSettings?.name || groupComponent || ''}
						/>
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>
	<div slot="footer" class="d-flex justify-content-end">
		{#if templateConfigStep.activeIndex > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					if (templateConfigStep.activeIndex > 0) {
						templateConfigStep = templateWizard[templateConfigStep.activeIndex - 1];
					}
				}}
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={templateConfigStep.activeIndex == templateWizard.length - 1 ? 'fas fa-save' : ''}
			rightIcon={templateConfigStep.activeIndex == templateWizard.length - 1
				? ''
				: 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			loading={waitForResponse}
			disabled={waitForResponse ||
				(templateConfigStep.activeIndex == 0 && !virtualTemplate?.type) ||
				(templateConfigStep.activeIndex == 1 && !validateVirtualTemplate(virtualTemplate))}
			on:click={() => {
				if (templateConfigStep.activeIndex == templateWizard.length - 1) {
					saveTemplate();
				} else {
					templateConfigStep = templateWizard[templateConfigStep.activeIndex + 1];
				}
			}}
		>
			{templateConfigStep.activeIndex == templateWizard.length - 1
				? $format('button_save', { default: 'Save' })
				: $format('button_next', { default: 'Next' })}
		</Button>
	</div>
</Modal>

{#if deviceTemplate}
	<DeleteTemplateModal
		{parentDevice}
		{virtualDevice}
		bind:showDeleteModal
		bind:showCreateTemplateModal
	/>
{/if}

<ExitConfirmationModal
	type={$format('vc_template_create_template', { default: 'Create Virtual Template Device' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	showAddEditModal={showCreateTemplateModal}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		showCreateTemplateModal = e.detail.showAddEditModal;
	}}
/>
